import React, { useCallback, useState } from 'react'
import { TouchableOpacity } from 'react-native'
import { graphql, useFragment } from 'react-relay'
import { useLocation } from 'react-router-dom'

import { PresentationMetadata, PresentationVideoPlayer } from '@components'

import { UserPrimaryPhoto } from '@newComponents/User/UserPrimaryPhoto'
import { FormattedMarkdown } from '@newComponents/FormattedMarkdown/FormattedMarkdown'

import { Icon } from '@newComponents/Icon'
import { Link } from '@newComponents/Link'
import { Text } from '@newComponents/Text'
import { H2 } from '@newComponents/Text'

import { styled, View, XStack, YStack } from 'tamagui'

import PresentationEvents from './PresentationEvents'
import PresentationGuilds from './PresentationGuilds'

import { PresentationScreenContent_presentation$key } from './__generated__/PresentationScreenContent_presentation.graphql'

export const PresentationScreenContent = (props: {
  presentation: PresentationScreenContent_presentation$key
}) => {
  const location = useLocation()
  const presentation = useFragment(
    graphql`
      fragment PresentationScreenContent_presentation on Presentation {
        canEdit
        description
        presenterFirstName
        presenterLastName
        prettyUrl
        title
        videoSourceUrl

        presenter {
          slugId
          firstName
          lastName

          ...UserPrimaryPhotoNew_user
          ...UserItem_user
        }
        ...PresentationMetadata_presentation
        ...PresentationVideoPlayer_presentation
        ...PresentationEvents_presentation
        ...PresentationGuilds_presentation
      }
    `,
    props.presentation
  )
  const [dropDownIsOpen, setDropDownIsOpen] = useState(false)
  const handlePress = useCallback(
    () => setDropDownIsOpen((value) => !value),
    []
  )
  const closeDropDown = useCallback(() => setDropDownIsOpen(false), [])

  let presenterElement

  if (presentation.presenter) {
    presenterElement = (
      <XStack gap='$1'>
        <Text size='$4' color='$grey7'>
          Presentation by
        </Text>
        <Link
          size='$4'
          href={`/${presentation.presenter.slugId}`}
          fontWeight='$5'
        >
          {presentation.presenter?.firstName} {presentation.presenter?.lastName}
        </Link>
      </XStack>
    )
  } else {
    presenterElement = (
      <XStack gap='$1'>
        <Text size='$4' color='$grey7'>
          Presentation by
        </Text>

        <Text size='$4' fontWeight='$5'>
          {presentation.presenterFirstName} {presentation.presenterLastName}
        </Text>
      </XStack>
    )
  }

  return (
    <YStack flex={1} flexWrap='wrap' maxWidth='100%' gap='$4'>
      <PresentationMetadata presentation={presentation} />

      <PresentationVideoPlayer presentation={presentation} />

      <YStack gap='$4' flex={1} maxWidth='100%'>
        <XStack zIndex={9}>
          <XStack flex={1} gap='$3'>
            {presentation.presenter && (
              <Link href={`/${presentation.presenter.slugId}`}>
                <UserPrimaryPhoto user={presentation.presenter} size='$12' />
              </Link>
            )}

            <YStack flex={1} gap='$2'>
              <H2>{presentation.title}</H2>

              {presenterElement}
            </YStack>
            {!!presentation.canEdit && (
              <YStack gap='$2'>
                <TouchableOpacity onPress={handlePress}>
                  <Icon
                    size='$6'
                    name={dropDownIsOpen ? 'chevron-up' : 'chevron-down'}
                  />
                </TouchableOpacity>
                {dropDownIsOpen && (
                  <YStack>
                    <View position='absolute' right={0}>
                      {!!presentation.canEdit && (
                        <Link
                          onPress={closeDropDown}
                          options={{
                            state: {
                              backgroundLocation:
                                location.state?.backgroundLocation || location,
                            },
                          }}
                          href={`/presentations/${presentation.prettyUrl}/edit`}
                        >
                          <XStack gap='$4'>
                            <Icon name='edit' />
                            <Text>Edit</Text>
                          </XStack>
                        </Link>
                      )}
                    </View>
                  </YStack>
                )}
              </YStack>
            )}
          </XStack>
        </XStack>

        {!!presentation.description && (
          <FormattedMarkdown textSize='$5'>
            {presentation.description}
          </FormattedMarkdown>
        )}
        <YStack gap='$4'>
          <PresentationEvents presentation={presentation} />
          <PresentationGuilds presentation={presentation} />
        </YStack>
      </YStack>
    </YStack>
  )
}

export default PresentationScreenContent
